import React, { useEffect, useState } from 'react';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  TextField, IconButton, Button, Typography
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import * as XLSX from 'xlsx';
import axios from 'axios';
import CryptoJS from 'crypto-js';
import qs from 'qs';
   import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const API_BASE_URL = "https://api.drivergems.com";

// Generate signature for the API request
function generateSignature(method, urlPath, timestamp) {
  const secretKey = "jnpkXDAM1GBvyDxYITtukz43zekx6UXgpipODwW9KY5";
  const message = `${method}${urlPath}${timestamp}`;
  return CryptoJS.HmacSHA256(message, secretKey).toString(CryptoJS.enc.Hex);
}

const formatDateTime = (dateString) => {
  if (!dateString || dateString === "N/A") return "N/A";
  const date = new Date(dateString);
  return date.toLocaleString("en-IN", {
    day: "numeric",
    month: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "numeric",
    second: "numeric",
    hour12: true,
    timeZone: "Asia/Kolkata"
  });
};

const ConsumerSummaryTable = ({ filter, fromDate, toDate, searchQuery = "", selectedRoId, onExportRef, onRefreshRef,onExportpdfRef }) => {
  const [summaryData, setSummaryData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: 'total_volume', direction: 'desc' });

  // Fetch the summary data based on filters and selected RO
  const fetchSummaryData = async () => {
    try {
      if (filter === "Select...") {
        setSummaryData([]);
        setFilteredData([]);
        return;
      }

      const token = localStorage.getItem("authToken");
      if (!token) {
        setSummaryData([]);
        setFilteredData([]);
        return;
      }

      // Use seconds for timestamp (consistent with other calls)
      const timestamp = Math.floor(Date.now() / 1000).toString();
      const urlPath = "/v2/user/transactions/fuel/range";
      const signature = generateSignature("GET", urlPath, timestamp);

      const params = {};
      if (fromDate) params.start_date = fromDate;
      if (toDate) params.end_date = toDate;
      if (selectedRoId) params.ro_id = selectedRoId; // send numeric if backend expects number

      const queryParams = qs.stringify(params);

      const response = await axios.get(`${API_BASE_URL}${urlPath}?${queryParams}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "X-Timestamp": timestamp,
          "X-Signature": signature
        },
      });

      const rawData = Array.isArray(response.data.transactions)
        ? response.data.transactions
        : Array.isArray(response.data)
          ? response.data
          : [];

      const summaryMap = new Map();

      rawData.forEach(row => {
        const fullName = `${row.user_firstname || ''} ${row.user_lastname || ''}`.trim();
        const fuelQty = parseFloat(row.quantity);
        if (isNaN(fuelQty)) return;
       const userKey = row.userid || row.phone;

        if (!summaryMap.has(fullName)) {
          summaryMap.set(fullName, {
            name: fullName,
            phone: row.phone || '',
            total_volume: fuelQty,
            total_transactions: 1,
            birthdate:row.user_dob || "N/A",
            outlet_name: row.ro_name || ''
          });
        } else {
          const userData = summaryMap.get(fullName);
          userData.total_volume += fuelQty;
          userData.total_transactions += 1;
        }
      });

      const summaryArray = Array.from(summaryMap.values());
      setSummaryData(summaryArray);
      setFilteredData(summaryArray);
    } catch (error) {
      console.error("Error fetching consumer summary:", error);
      setSummaryData([]);
      setFilteredData([]);
    }
  };

  // Re-filter when searchQuery, selectedRoId or summary data changes
  useEffect(() => {
    const lowerSearch = (searchQuery || "").toLowerCase();
    setFilteredData(
      summaryData.filter(item =>
        (item.name || "").toLowerCase().includes(lowerSearch) ||
        (item.phone || "").toLowerCase().includes(lowerSearch)
      )
    );
  }, [searchQuery, selectedRoId, summaryData]);

  // Keep refresh ref pointing to the latest fetch function
  useEffect(() => {
    if (onRefreshRef) onRefreshRef.current = fetchSummaryData;
  }, [onRefreshRef, fetchSummaryData, selectedRoId, fromDate, toDate, filter]);

  // Export ref uses the latest filteredData
  useEffect(() => {
    if (onExportRef) onExportRef.current = () => {
      const exportData = filteredData.map(u => ({
        Name: u.name || "",
        Phone: u.phone || "N/A",
        "Total Fuel Volume(Litre/KG)": u.total_volume || 0,
        "Total Transactions": u.total_transactions,
         "BirthDate":formatDOB(u.birthdate),
        "Outlet Name": u.outlet_name || ""
      }));
      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Consumer summary Table");
      XLSX.writeFile(workbook, "consumer_summary.xlsx");
    };
  }, [onExportRef, filteredData]);



useEffect(() => {
  if (onExportpdfRef)
    onExportpdfRef.current = () => {

      if (!filteredData || filteredData.length === 0) {
        alert("No data to export!");
        return;
      }

      const exportpdfData = filteredData.map(u => ({
        Name: u.name || "",
        Phone: u.phone || "N/A",
        "Total Fuel Volume(Litre/KG)": u.total_volume || 0,
        "Total Transactions": u.total_transactions,
        "BirthDate":formatDOB(u.birthdate),
        "Outlet Name": u.outlet_name || ""
      }));

      // Convert keys to table headers
      const headers = Object.keys(exportpdfData[0]);

      // Convert to rows
      const body = exportpdfData.map(row => Object.values(row));

      // Create PDF
      const doc = new jsPDF({
        orientation: "portrait",
        unit: "pt",
        format: "a4"
      });

      // Title
      doc.setFontSize(14);
      doc.text("Consumer Summary Report", 40, 40);

      // Table
      autoTable(doc, {
        startY: 60,
        head: [headers],
        body: body,
        styles: { fontSize: 10, cellPadding: 6 },
        headStyles: {
          fillColor: [240, 240, 240],
          textColor: 20,
          fontStyle: "bold",
        },
        margin: { left: 40, right: 20 },
        didDrawPage: (dataArg) => {
          // Footer: page number
          let pageSize = doc.internal.pageSize;
          let pageHeight = pageSize.height || pageSize.getHeight();
          doc.setFontSize(9);
          doc.text(
            `Page ${dataArg.pageNumber}`,
            pageSize.width - 60,
            pageHeight - 10
          );
        }
      });

      // Save file
      doc.save("consumer_summary.pdf");
    };
}, [onExportpdfRef, filteredData]);


  // Fetch data when filters or selectedRoId change
  useEffect(() => {
    fetchSummaryData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, fromDate, toDate, selectedRoId]);

  // Sort data based on the selected column and direction (only for total_volume and total_transactions)
  const handleSort = (key) => {
    if (key !== 'total_volume' && key !== 'total_transactions') return; // Only allow sorting on these columns

    const direction = (sortConfig.key === key && sortConfig.direction === 'asc') ? 'desc' : 'asc';
    setSortConfig({ key, direction });
  };

  // Sort filtered data based on the selected column and direction
  const sortedData = [...filteredData].sort((a, b) => {
    if (a[sortConfig.key] < b[sortConfig.key]) return sortConfig.direction === 'asc' ? -1 : 1;
    if (a[sortConfig.key] > b[sortConfig.key]) return sortConfig.direction === 'asc' ? 1 : -1;
    return 0;
  });
  
  const formatDOB = (dob) => {
  if (!dob || dob === "N/A") return "-";

  let date;

  // Try direct Date parsing (ISO / timestamp)
  const d1 = new Date(dob);
  if (!isNaN(d1.getTime())) {
    date = d1;
  } else {
    // Handle DD-MM-YYYY or DD/MM/YYYY
    const parts = dob.split(/[-/]/);
    if (parts.length === 3) {
      const [day, month, year] = parts;
      const d2 = new Date(`${year}-${month}-${day}`);
      if (!isNaN(d2.getTime())) {
        date = d2;
      }
    }
  }

  if (!date) return "-";

  const day = date.getDate();
  const month = date.toLocaleString("en-IN", { month: "short" });

  // Ordinal suffix logic
  const getSuffix = (d) => {
    if (d > 3 && d < 21) return "th";
    switch (d % 10) {
      case 1: return "st";
      case 2: return "nd";
      case 3: return "rd";
      default: return "th";
    }
  };

  return `${day}${getSuffix(day)} ${month}`;
};

  return (
    <TableContainer component={Paper}>
      <Table sx={{ minWidth: 600 }} size="small">
        <TableHead>
          <TableRow style={{ background: "lightgrey" }}>
            <TableCell style={{ border: "2px solid #a7a4a4" }}>
              <strong>Name</strong>
            </TableCell>
            <TableCell style={{ border: "2px solid #a7a4a4" }}>
              <strong>Phone Number</strong>
            </TableCell>
            <TableCell onClick={() => handleSort('total_volume')} style={{ cursor: 'pointer', border: "2px solid #a7a4a4" }}>
              <strong>
                Total Fuel Volume(Litre/KG) { (sortConfig.key === 'total_volume' ? (sortConfig.direction === 'asc' ? '🔼' : '🔽') : '') }
              </strong>
            </TableCell>
            <TableCell onClick={() => handleSort('total_transactions')} style={{ cursor: 'pointer', border: "2px solid #a7a4a4" }}>
              <strong>
                Total Transactions { (sortConfig.key === 'total_transactions' ? (sortConfig.direction === 'asc' ? '🔼' : '🔽') : '') }
              </strong>
            </TableCell>
             <TableCell style={{ border: "2px solid #a7a4a4" }}>
              <strong>
               Birth Date
              </strong>
            </TableCell>
            <TableCell style={{ border: "2px solid #a7a4a4" }}>
              <strong>Outlet Name</strong>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedData.length > 0 ? (
            sortedData.map((row, index) => (
              <TableRow key={index}>
                <TableCell>{row.name}</TableCell>
                <TableCell>{row.phone}</TableCell>
                <TableCell>{(row.total_volume || 0).toFixed(2)}</TableCell>
                <TableCell>{row.total_transactions}</TableCell>
                <TableCell> {formatDOB(row.birthdate)}</TableCell>
                <TableCell>{row.outlet_name}</TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={5} align="center">No data available</TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default ConsumerSummaryTable;
